import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';
import { useAtom } from 'jotai';
import { useUpdateAtom } from 'jotai/utils';
import { useHistory } from 'react-router-dom';
import { chatOpenAtom, chatHistoryViewAtom, chatSessionResetRequestAtom } from 'src/shared/chat';
import { chatActionModeAtom, chatMaximizedAtom } from 'src/shared/chatPreferences';
import ExternalAgentMenu from 'src/components/ExternalAgentMenu';
import styles from './ChatHeader.module.scss';

export const ChatHeader: React.FC = () => {
  const [, setChatOpen] = useAtom(chatOpenAtom);
  const requestNewSession = useUpdateAtom(chatSessionResetRequestAtom);
  const [historyView, setHistoryView] = useAtom(chatHistoryViewAtom);
  const [actionMode, setActionMode] = useAtom(chatActionModeAtom);
  const [isMaximized, setMaximized] = useAtom(chatMaximizedAtom);
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const handleOpenSchedules = () => {
    setChatOpen(false);
    history.push('/ai/schedules');
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    if (showSettings) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  // Requests the reset rather than clearing the atoms here. Clearing directly
  // left a live turn streaming into the empty list and dropped the pending
  // auto-save, so the last turn of the abandoned chat never reached history.
  const handleNewChat = () => {
    requestNewSession((n) => n + 1);
  };

  const handleClose = () => {
    setChatOpen(false);
  };

  return (
    <div className={styles.header}>
      <div className={styles.titleRow}>
        <div className={styles.title}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className={styles.titleIcon}>
            <path
              d="M13 2l2 6.5L21 11l-6 2.5L13 20l-2-6.5L5 11l6-2.5L13 2z"
              fill="var(--color-prime-400)"
            />
            <path
              d="M5.5 17l.75 2.25L8.5 20l-2.25.75L5.5 23l-.75-2.25L2.5 20l2.25-.75L5.5 17z"
              fill="var(--color-prime-400)"
            />
          </svg>
          <span>AI Agent</span>
        </div>
        <div className={styles.actions}>
          <button
            className={styles.iconBtn}
            onClick={handleNewChat}
            title="New chat"
            aria-label="New chat"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button
            className={styles.iconBtn}
            onClick={() => setHistoryView(!historyView)}
            title={historyView ? 'Back to chat' : 'Chat history'}
            aria-label={historyView ? 'Back to chat' : 'Chat history'}
            data-test="chat-history-toggle"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
              <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            className={styles.iconBtn}
            onClick={handleOpenSchedules}
            title="Scheduled agents"
            aria-label="Scheduled agents"
            data-test="chat-schedules-link"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M3 9h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M8 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M16 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="15.5" cy="16.5" r="4" stroke="currentColor" strokeWidth="1.5" />
              <path d="M15.5 15v2l1.5 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <ExternalAgentMenu />
          <div className={styles.settingsWrapper} ref={settingsRef}>
            <button
              className={styles.iconBtn}
              onClick={() => setShowSettings(!showSettings)}
              title="Settings"
              aria-label="Chat settings"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 15a3 3 0 100-6 3 3 0 000 6z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
            {showSettings && (
              <div className={styles.settingsPopover}>
                <div className={styles.settingsTitle}>After tool execution</div>
                <label className={styles.settingsOption}>
                  <input
                    type="radio"
                    name="actionMode"
                    checked={actionMode === 'navigate'}
                    onChange={() => setActionMode('navigate')}
                  />
                  <div>
                    <div className={styles.optionLabel}>Navigate to result</div>
                    <div className={styles.optionDesc}>Go to the created/updated page</div>
                  </div>
                </label>
                <label className={styles.settingsOption}>
                  <input
                    type="radio"
                    name="actionMode"
                    checked={actionMode === 'toast'}
                    onChange={() => setActionMode('toast')}
                  />
                  <div>
                    <div className={styles.optionLabel}>Stay on page</div>
                    <div className={styles.optionDesc}>Show a notification with a link</div>
                  </div>
                </label>
              </div>
            )}
          </div>
          <button
            className={cn(styles.iconBtn, styles.maximizeBtn)}
            onClick={() => setMaximized((prev) => !prev)}
            title={isMaximized ? 'Restore chat size' : 'Maximize chat'}
            aria-label={isMaximized ? 'Restore chat size' : 'Maximize chat'}
            data-test="chat-maximize-toggle"
          >
            {isMaximized ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 4v3a2 2 0 01-2 2H4M15 4v3a2 2 0 002 2h3M9 20v-3a2 2 0 00-2-2H4M15 20v-3a2 2 0 012-2h3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 9V6a2 2 0 012-2h3M20 9V6a2 2 0 00-2-2h-3M4 15v3a2 2 0 002 2h3M20 15v3a2 2 0 01-2 2h-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <button
            className={cn(styles.iconBtn, styles.closeBtn)}
            onClick={handleClose}
            title="Close"
            aria-label="Close chat"
            data-test="chat-close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
